import React, { useState } from "react";
import { api, errorMessage } from "../lib/api";
import { Award, ShieldCheck, Linkedin, BadgeCheck, Search, CheckCircle2, XCircle, Sparkles } from "lucide-react";

const TIERS = [
  { label: "Participation", desc: "Every member who submits a valid Round 1 entry.", color: "text-white", dot: "bg-gray-400" },
  { label: "Finalist", desc: "Teams that advance to the Round 2 finale.", color: "text-brand", dot: "bg-brand" },
  { label: "Certificate of Excellence", desc: "Awarded to the winning teams — rank-stamped.", color: "text-amber-300", dot: "bg-amber-400" },
];

// A CSS-coded certificate preview — uses the real brand fonts/colours (no stock image).
function CertificateMockup() {
  return (
    <div className="relative" data-testid="cert-mockup">
      <div className="absolute -inset-0.5 bg-gradient-to-br from-brand/40 via-transparent to-amber-400/20 blur-xl opacity-60" aria-hidden />
      <div className="relative bg-[#0A0F1E] border border-brand/30 p-6 sm:p-8 shadow-2xl overflow-hidden">
        <div className="absolute inset-2 border border-white/10 pointer-events-none" aria-hidden />
        <div className="absolute top-0 right-0 w-28 h-28 bg-brand/10 blur-3xl rounded-full" aria-hidden />
        <div className="relative">
          <div className="flex items-center justify-between">
            <div>
              <div className="flex items-baseline gap-1">
                <span className="font-display text-lg tracking-tighter text-white">NoobSync</span>
                <span className="font-display text-lg tracking-tighter text-brand">Innovate</span>
              </div>
              <div className="label-mono mt-0.5 text-[10px]">IdeaThon Edition</div>
            </div>
            <Award className="w-10 h-10 text-amber-300" strokeWidth={1.25} />
          </div>

          <div className="mt-8 text-center">
            <div className="label-mono text-brand text-[10px]">Certificate of Participation</div>
            <div className="text-[11px] text-gray-500 mt-3 font-mono uppercase tracking-widest">This is proudly presented to</div>
            <div className="font-display text-2xl sm:text-3xl text-white mt-2">Your Name Here</div>
            <div className="w-24 h-px bg-brand/50 mx-auto mt-3" />
            <p className="text-xs text-gray-400 mt-4 max-w-sm mx-auto leading-relaxed">
              for participating in <span className="text-white">IdeaThon 2026</span> and
              completing a valid Round 1 submission.
            </p>
          </div>

          <div className="mt-8 flex items-end justify-between">
            <div className="text-left">
              <div className="text-[10px] text-gray-500 font-mono">VERIFICATION ID</div>
              <div className="text-xs text-brand font-mono tracking-widest">NSI-2026-A1B2</div>
            </div>
            <div className="text-right">
              <div className="font-display text-sm text-white border-b border-white/20 pb-1">NoobSync Innovations</div>
              <div className="text-[10px] text-gray-500 font-mono mt-1">Organising Authority</div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function VerifyWidget() {
  const [code, setCode] = useState("");
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const verify = async (e) => {
    e?.preventDefault();
    const id = code.trim().toUpperCase();
    if (!id) return;
    setLoading(true); setError(""); setResult(null);
    try {
      const { data } = await api.get(`/certificates/verify/${encodeURIComponent(id)}`);
      setResult(data);
    } catch (err) {
      setError(errorMessage(err) || "No certificate found for this ID");
    } finally { setLoading(false); }
  };

  return (
    <div className="card-surface" data-testid="landing-verify-widget">
      <div className="flex items-center gap-2 label-eyebrow mb-3">
        <ShieldCheck className="w-4 h-4 text-brand" /> Verify a certificate
      </div>
      <p className="text-sm text-gray-400 mb-4">Anyone can confirm a certificate&apos;s authenticity in seconds. Enter a verification ID (e.g. <span className="font-mono text-gray-300">NSI-2026-XXXX</span>).</p>
      <form onSubmit={verify} className="flex gap-2">
        <input
          value={code}
          onChange={(e) => setCode(e.target.value)}
          placeholder="NSI-2026-XXXX"
          className="input-field flex-1 font-mono uppercase tracking-widest"
          data-testid="landing-verify-input"
        />
        <button type="submit" disabled={loading} className="btn-primary flex items-center gap-2 whitespace-nowrap" data-testid="landing-verify-submit">
          {loading ? <span className="spinner" /> : <Search className="w-4 h-4" />} Verify
        </button>
      </form>
      {error && (
        <div className="mt-4 flex items-center gap-2 text-sm text-red-400" data-testid="landing-verify-error">
          <XCircle className="w-4 h-4 flex-shrink-0" /> {error}
        </div>
      )}
      {result && (
        <div className="mt-4 border-l-2 border-emerald-500 pl-4 py-1" data-testid="landing-verify-result">
          <div className="flex items-center gap-2 text-emerald-400 text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" /> Authentic · {result.label}
          </div>
          <div className="text-sm text-white mt-1">{result.holder_name}{result.team_name ? ` · ${result.team_name}` : ""}</div>
          <div className="text-xs text-gray-500 font-mono mt-0.5">{result.edition_label} · {result.verification_id}</div>
        </div>
      )}
    </div>
  );
}

export default function CertificatesSection() {
  return (
    <section id="certificates" className="max-w-7xl mx-auto px-5 sm:px-8 py-6 md:py-8 lg:py-8" data-testid="certificates-section">
      <div className="grid lg:grid-cols-12 gap-12 items-center">
        <div className="lg:col-span-6">
          <div className="label-eyebrow mb-3">Certificates</div>
          <h2 className="font-display text-4xl sm:text-5xl text-white leading-none">
            Verifiable certificates.
          </h2>
          <p className="text-gray-400 mt-4 max-w-md">
            Participants who submit a valid Round 1 entry receive a verifiable certificate
            issued by NoobSync Innovations after results are announced.
          </p>

          <div className="mt-6 space-y-3">
            {TIERS.map((t, i) => (
              <div key={i} className="flex items-start gap-3" data-testid={`cert-tier-${i}`}>
                <span className={`mt-1.5 w-2 h-2 rounded-full ${t.dot} flex-shrink-0`} />
                <div>
                  <div className={`font-display text-lg ${t.color}`}>{t.label}</div>
                  <div className="text-sm text-gray-400">{t.desc}</div>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-6 flex flex-wrap gap-3">
            <div className="inline-flex items-center gap-2 text-xs font-mono px-3 py-2 border border-brand/30 bg-brand/5 text-brand" data-testid="cert-badge-recognized">
              <Sparkles className="w-3.5 h-3.5" /> Issued by NoobSync Innovations
            </div>
            <div className="inline-flex items-center gap-2 text-xs font-mono px-3 py-2 border border-white/10 text-gray-300" data-testid="cert-badge-verifiable">
              <BadgeCheck className="w-3.5 h-3.5 text-emerald-400" /> Verifiable ID
            </div>
            <div className="inline-flex items-center gap-2 text-xs font-mono px-3 py-2 border border-white/10 text-gray-300" data-testid="cert-badge-linkedin">
              <Linkedin className="w-3.5 h-3.5 text-[#0A66C2]" /> Add to LinkedIn
            </div>
          </div>
        </div>

        <div className="lg:col-span-6 space-y-6">
          <CertificateMockup />
          <VerifyWidget />
        </div>
      </div>
    </section>
  );
}
